import { useCallback, useRef } from "react";
import { IColumn } from "@cayo/shared/api/types/schema";
import { isString } from "@cayo/shared/types/typeGuards";
import { fetchObjectsFunc, fetchObjectsParams } from "./logic";
import { IQueryPickerClientBehavior } from "../../api/schema.api";

const filterItems = (
  items: any[],
  searchText: string | undefined,
  desc: boolean = false,
  orderBy: string | string[] | null | undefined,
  fieldNames: string[]
) => {
  let result = [...items];

  if (searchText) {
    const lookingText = searchText?.toLowerCase().trim();
    result = result.filter((obj) =>
      fieldNames.some((fieldName) => obj[fieldName]?.toLowerCase().includes(lookingText))
    );
  }

  if (orderBy) {
    const columnKey = isString(orderBy) ? orderBy : orderBy[0];
    result = result.sort((a, b) =>
      (desc ? a[columnKey] < b[columnKey] : a[columnKey] > b[columnKey]) ? 1 : -1
    );
  }

  return result;
};

export const usePickerObjects = (
  fetchObjects: fetchObjectsFunc,
  columns: IColumn[] | undefined,
  clientBehavior: IQueryPickerClientBehavior | undefined
) => {
  const cachedResultRef = useRef<Awaited<ReturnType<fetchObjectsFunc>> | undefined>(undefined);

  /**
   * Fetches objects with optional caching and client-side search capabilities.
   *
   * This function first checks if caching is enabled and a cached result exists; if so, it returns the cached result.
   * Otherwise, it fetches new data using the provided `fetchObjects` function and caches the result if caching is enabled.
   * If client-side search is enabled, it filters the result based on the provided search text and supported columns.
   *
   * @param prms - The parameters for fetching objects, including search text and other query options.
   * @returns A promise that resolves to the fetched (and possibly filtered) objects.
   */
  const cachedFetchObjects = useCallback(
    async (prms: fetchObjectsParams) => {
      let result;
      if (clientBehavior?.cacheSearchResponse && cachedResultRef.current) {
        result = cachedResultRef.current!; // Return the cached result
      } else {
        result = await fetchObjects(prms);

        if (clientBehavior?.cacheSearchResponse) {
          cachedResultRef.current = result;
        }
      }

      if (clientBehavior?.clientSideSearch) {
        const fieldNames: string[] =
          columns?.filter((c) => c.supportsClientSearch).map((c) => c.fieldName) || [];

        if (fieldNames.length > 0) {
          const { searchText, desc, orderBy } = prms;
          result = filterItems(result, searchText, desc, orderBy, fieldNames);
        }
      }

      return result;
    },
    [fetchObjects]
  );

  return cachedFetchObjects;
};
